import type { ReplayPlugin } from "@slot/client-engine";
import type { RoundLifecycle } from "../../composition/RoundLifecycle";
import type { FeatureFinishedPresenter } from "./FeatureFinishedPresenter";

/** The transition presenter the finished panel hands over to on dismiss. */
export interface FeatureFinishedTransition {
  presentTransition(): Promise<void>;
}

/**
 * The feature-finished panel at a feature exit: shown with the round's total; a positive-win round
 * is acknowledged early (as soon as the panel is ready) so the settlement is not held by the panel;
 * dismissing it plays the mode transition.
 */
export function createFeatureFinishedPlugin(deps: {
  presenter: FeatureFinishedPresenter;
  transition: FeatureFinishedTransition;
  /** Absent in replay: a replayed round is never settled. */
  lifecycle: RoundLifecycle | null;
}): ReplayPlugin {
  return {
    name: "feature-finished",
    observer: {
      onFeatureState: async (event) => {
        const featureState = event.eventContent.featureState;
        if (featureState?.featurePhase !== "exit") return;

        const winIsPositive = BigInt(event.accumulatedWinAmountAfterEvent ?? "0") > 0n;
        const lifecycle = deps.lifecycle;
        const earlyAck = winIsPositive && lifecycle ? () => lifecycle.acknowledgePresentationComplete(event.roundIdentifier) : undefined;

        await deps.presenter.presentFeatureFinished({
          totalWinMinor: event.accumulatedWinAmountAfterEvent,
          featureIdentifier: featureState.featureIdentifier,
          onPresentationReady: earlyAck,
          onUserDismiss: () => {
            void deps.transition.presentTransition();
          },
        });
      },
    },
  };
}
